// frontend/src/components/ui/ErrorAlert.tsx
import React from 'react';
import { cn } from '@/utils';

export interface ErrorAlertProps {
  message: string;
  onClose?: () => void;
  className?: string;
  variant?: 'error' | 'warning' | 'info' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  actions?: React.ReactNode;
  title?: string;
}

export const ErrorAlert: React.FC<ErrorAlertProps> = ({
  message,
  onClose,
  className,
  variant = 'error',
  size = 'md',
  fullWidth = false,
  actions,
  title
}) => {
  const variantClasses = {
    error: 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300',
    info: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300',
    success: 'bg-green-50 border-green-200 text-green-700 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300'
  };

  const iconClasses = {
    error: 'text-red-400 dark:text-red-300',
    warning: 'text-yellow-400 dark:text-yellow-300',
    info: 'text-blue-400 dark:text-blue-300',
    success: 'text-green-400 dark:text-green-300'
  };

  const sizeClasses = {
    sm: 'p-3 text-xs sm:text-sm',
    md: 'p-4 text-sm',
    lg: 'p-6 text-sm sm:text-base'
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const getIcon = () => {
    const iconSize = iconSizeClasses[size];
    const iconColor = iconClasses[variant];
    
    switch (variant) {
      case 'error':
        return (
          <svg className={cn(iconSize, iconColor)} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className={cn(iconSize, iconColor)} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
        return (
          <svg className={cn(iconSize, iconColor)} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        );
      case 'success':
        return (
          <svg className={cn(iconSize, iconColor)} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L8.23 10.97a.75.75 0 00-1.06 1.061l1.5 1.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div className={cn(
      'border rounded-md',
      variantClasses[variant],
      sizeClasses[size],
      fullWidth ? 'w-full' : '',
      className
    )}>
      <div className="flex">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        
        <div className="ml-3 flex-1 min-w-0">
          {title && (
            <h3 className="font-medium mb-1 break-words">
              {title}
            </h3>
          )}
          <p className={cn(
            title ? 'text-opacity-90' : 'font-medium',
            'break-words'
          )}>
            {message}
          </p>
          
          {actions && (
            <div className="mt-3 flex flex-wrap gap-2">
              {actions}
            </div>
          )}
        </div>
        
        {onClose && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                onClick={onClose}
                className={cn(
                  'inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2',
                  'transition-colors duration-200 touch-manipulation',
                  // Touch-friendly minimum size
                  'min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0',
                  variant === 'error' && 'text-red-400 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50 dark:hover:bg-red-900/40 dark:focus:ring-offset-red-900/20',
                  variant === 'warning' && 'text-yellow-400 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50 dark:hover:bg-yellow-900/40 dark:focus:ring-offset-yellow-900/20',
                  variant === 'info' && 'text-blue-400 hover:bg-blue-100 focus:ring-blue-600 focus:ring-offset-blue-50 dark:hover:bg-blue-900/40 dark:focus:ring-offset-blue-900/20',
                  variant === 'success' && 'text-green-400 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50 dark:hover:bg-green-900/40 dark:focus:ring-offset-green-900/20'
                )}
              >
                <span className="sr-only">Dismiss</span>
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};